/** @jsxImportSource @emotion/react */
import * as React from "react";
import { SpinnerBlock } from "../../common/Spinner";
import { OrganizationKeyName } from "../../../model/Organization";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { colors } from "../../../components/colors";
import { PrimaryButton } from "../../../components/Buttons";
import { StyledText } from "../../../assets/typography/typography";
import { FormRow } from "../../common/hook-form/index";
import { UserBenefit } from "./UserBenefit";
import { CircleContainer } from "../../../components/CircleContainer";
import { VerticalDivider } from "../../../components/VerticalDivider";
import { CardHeader } from "../../../components/CardHeader";
import { Placeholder } from "../../../components/Placeholder";
import { formatDateWithYear } from "../../../util";
import moment from "moment";
import { useDeleteUserVoucher, useGetUserVouchers } from "../../../service/api";
import { toastr } from "react-redux-toastr";
import { SelectField } from "../../../components/forms/fields/SelectField";
import { AddVoucherToUserSelector } from "./AddVoucherToUserSelector";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { css } from "@emotion/react";
import { breakpoints } from "../../../components/breakpoints";

interface Props {
  userId: string;
  organization: OrganizationKeyName;
}

const mobileWrapperStyle = css`
  ${breakpoints.mobile} {
    margin-top: 24px;
  }
`;

const voucherStatusOptions = [
  { label: "Any", value: "" },
  { label: "Active", value: "ACTIVE" },
  { label: "Redeemed", value: "REDEEMED" },
  { label: "Expired", value: "EXPIRED" },
];

export const UserProfileVoucherListPartial: React.FC<Props> = (props) => {
  const { userId, organization } = props;
  const { t } = useTranslation();

  const returnVoucherType = (type: string) => {
    switch (type) {
      case "DISCOUNT_TYPE_PERCENTAGE_PRODUCT":
        return "Percentage product";
      case "DISCOUNT_TYPE_VALUE_ORDER":
        return "Value order";
      case "DISCOUNT_TYPE_PERCENTAGE_ORDER":
        return "Percentage order";
      default:
        return type;
    }
  };

  const returnVoucherDiscount = (type: string, value: number) => {
    switch (type) {
      case "DISCOUNT_TYPE_VALUE_ORDER":
        return value / 100 + " kr.";
      default:
        return value + "%";
    }
  };

  const formMethods = useForm({
    defaultValues: {
      filters: {
        filterByStatus: "ACTIVE",
      },
    },
  });

  const { watch, control } = formMethods;

  const {
    field: { onChange, value },
  } = useController({
    control,
    name: "filters.filterByStatus",
  });

  const {
    data: userVouchers,
    isLoading: isLoadingUserVouchers,
    error: userVouchersError,
  } = useGetUserVouchers(organization, watch("filters"), userId);

  useHandleFetchError(userVouchersError);

  const { mutateAsync: deleteVoucher } = useDeleteUserVoucher();

  return (
    <>
      <div css={mobileWrapperStyle} className="card">
        <CardHeader title="Vouchers">
          <div className="d-flex flex-row">
            <PrimaryButton
              type="button"
              color={colors.textPrimary}
              backgroundColor={colors.white}
              hoverColor={colors.ctaLight}
              onClick={() => {
                onChange("");
              }}
            >
              Reset filter
            </PrimaryButton>
            <AddVoucherToUserSelector userId={userId} voucherId={"__new"} />
          </div>
        </CardHeader>
        <div className="card-body d-flex flex-column" style={{ gap: "16px" }}>
          <form>
            <FormRow>
              <SelectField
                label="Filter by voucher status"
                value={voucherStatusOptions?.find(
                  (option) => option.value === value
                )}
                onChange={(value) => {
                  const newValue = value as { value: string; label: string };
                  onChange(newValue.value);
                }}
                options={voucherStatusOptions}
              />
            </FormRow>
          </form>
          {isLoadingUserVouchers ? (
            <SpinnerBlock />
          ) : (
            <>
              {userVouchers && userVouchers.length === 0 ? (
                <Placeholder>User has no vouchers.</Placeholder>
              ) : (
                <>
                  {userVouchers?.map((x) => (
                    <UserBenefit
                      name={x.name}
                      key={x.id}
                      type="voucher"
                      isRedeemed={x.isRedeemed}
                      remove={async () => {
                        try {
                          await deleteVoucher({
                            id: x.id,
                            organizationKeyName: organization,
                          });
                          toastr.success(
                            "Success",
                            "Voucher deleted successfully"
                          );
                        } catch (error: unknown) {
                          const errorMessage = getErrorMessage(error);
                          toastr.error("Error", errorMessage);
                        }
                      }}
                      active={!x.isExpired}
                    >
                      <div
                        className="d-flex flex-column"
                        style={{ gap: "8px" }}
                      >
                        <div
                          className="d-flex flex-row align-items-center"
                          style={{ gap: "8px" }}
                        >
                          <CircleContainer
                            height="4px"
                            width="4px"
                            backgroundColor={
                              !x.isDeleted && !x.isExpired && !x.isRedeemed
                                ? colors.successMedium
                                : colors.errorMedium
                            }
                          />
                          <StyledText
                            color={colors.textPrimary}
                            className="bodySubtextRegular mb-0"
                          >
                            {!x.isDeleted &&
                              !x.isExpired &&
                              !x.isRedeemed &&
                              "Active"}
                            {x.isDeleted && "Deleted"}
                            {x.isExpired &&
                              `Expired at ${moment(x.expiresAt).format(
                                "DD/MM/YYYY"
                              )}`}
                          </StyledText>
                          <VerticalDivider style={{ height: "16px" }} />
                          <StyledText
                            color={colors.textPrimary}
                            className="bodySubtextRegular mb-0"
                          >
                            {returnVoucherType(x.discountType)}
                          </StyledText>
                          <VerticalDivider style={{ height: "16px" }} />
                          <StyledText
                            color={colors.textPrimary}
                            className="bodySubtextRegular mb-0"
                          >
                            {returnVoucherDiscount(
                              x.discountType,
                              x.discountValue
                            )}
                          </StyledText>
                          {!x.isExpired && (
                            <>
                              <VerticalDivider style={{ height: "16px" }} />
                              <StyledText
                                color={colors.textPrimary}
                                className="bodySubtextRegular mb-0"
                              >
                                Expires at{" "}
                                <>
                                  {t(
                                    "user_profile.voucher_list.row.expires_at",
                                    {
                                      date: formatDateWithYear(x.expiresAt),
                                    }
                                  )}
                                </>
                              </StyledText>
                            </>
                          )}
                        </div>
                      </div>
                    </UserBenefit>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfileVoucherListPartial;
